import { useState } from 'react'
import {
  archiveBlockDirect,
  enqueueWrapup,
  type CockpitAttentionGroup,
  type CockpitPayload,
  type CockpitWorkstream,
} from '@/lib/api'
import { cn } from '@/lib/utils'
import { STATUS_COLORS, domainColor, fmtDay, fmtMin, fmtTime, relTime, stripTimestamp } from '../lib'
import { DayStrip } from './DayStrip'

type OpenFn = (path: string, name?: string, meta?: string) => void

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h3 className="text-[13px] text-foreground mt-6 mb-1.5">{children}</h3>
}

function Row({ children, sub }: { children: React.ReactNode; sub?: boolean }) {
  return (
    <div
      className={cn(
        'flex items-center gap-2.5 px-1.5 py-1 min-h-[26px] min-w-0 hover:bg-white/[0.04]',
        sub && 'ml-6'
      )}
    >
      {children}
    </div>
  )
}

const nameCls = 'cursor-pointer text-[13px] text-foreground truncate hover:underline text-left'
const metaCls = 'ml-auto flex-shrink-0 text-[11px] text-muted-foreground truncate max-w-[40%]'
const whenCls = 'flex-shrink-0 text-[11px] text-muted-foreground tabular-nums'
const miniBtnCls =
  'flex-shrink-0 text-[10px] px-2 py-0.5 border border-border text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40'

/** Board tab — port of the plugin CockpitView's _renderBoard (the Time tab
 * is deliberately not ported). */
export function CockpitBoard({
  data,
  domain,
  setDomain,
  matches,
  onOpen,
  refresh,
}: {
  data: CockpitPayload
  domain: string | null
  setDomain: (d: string | null) => void
  matches: (...parts: Array<string | null | undefined>) => boolean
  onOpen: OpenFn
  refresh: () => void
}) {
  const inDom = (x: { domain: string }) => !domain || x.domain === domain
  const h = data.hero
  const today = data.timeline?.today

  // ── TODAY: KPI line + day strip + now/next ────────────────────────────
  const kpiBits = [`tracked ${fmtMin(h.today.minutes)}`]
  if (today?.kpis) {
    const k = today.kpis
    if (k.committed_min > 0) {
      kpiBits.push(`committed ${fmtMin(k.committed_min)}`)
      kpiBits.push(`filled ${k.plan_pct == null ? '—' : `${k.plan_pct}%`}`)
      if (k.unmet_min > 1) kpiBits.push(`unmet ${fmtMin(k.unmet_min)}`)
    }
    if (k.void_min != null) kpiBits.push(`void ${fmtMin(k.void_min)}`)
  }
  const delta = h.last24h.delta_pct
  const statsTxt = `week ${fmtMin(h.week.minutes)} · 24h ${fmtMin(h.last24h.minutes)}${
    delta == null ? '' : ` (${delta > 0 ? '↑' : delta < 0 ? '↓' : '→'}${Math.abs(delta)}%)`
  }`

  return (
    <div>
      <section>
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <span className="text-[13px] text-foreground">
            {new Date(data.generated_at).toLocaleDateString(undefined, {
              weekday: 'long',
              month: 'short',
              day: 'numeric',
            })}
          </span>
          <span className="text-[11px] text-muted-foreground">{kpiBits.join(' · ')}</span>
        </div>
        {today && (
          <div className="mt-2">
            <DayStrip day={today} showNow onOpen={onOpen} />
          </div>
        )}
        <div className="flex items-center gap-4 flex-wrap mt-1 text-[12px]">
          {h.now ? (
            <button type="button" className={nameCls} onClick={() => onOpen(h.now!.path, h.now!.name)}>
              NOW {stripTimestamp(h.now.name)} · until {fmtTime(h.now.end)}
            </button>
          ) : (
            <span className="text-muted-foreground">NOW idle</span>
          )}
          {h.next && (
            <button type="button" className={nameCls} onClick={() => onOpen(h.next!.path, h.next!.name)}>
              → next: {stripTimestamp(h.next.name)} · {fmtDay(h.next.start)} {fmtTime(h.next.start)}
            </button>
          )}
          <span className="text-[11px] text-muted-foreground ml-auto">{statsTxt}</span>
        </div>
      </section>

      <AttentionZone data={data} inDom={inDom} matches={matches} onOpen={onOpen} refresh={refresh} />

      {/* ── Pick up where you left off ── */}
      <RecentZone data={data} inDom={inDom} matches={matches} onOpen={onOpen} />

      {/* ── Collapsed: Workstreams ── */}
      <details className="mt-5 border border-border px-3 py-2" open={!!domain}>
        <summary className="cursor-pointer select-none text-[13px] text-foreground">Workstreams</summary>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 my-2">
          {data.domains.map((t) => (
            <button
              key={t.name}
              type="button"
              onClick={() => setDomain(domain === t.name ? null : t.name)}
              className={cn(
                'border px-3 py-2 text-left min-w-0',
                domain === t.name ? 'border-foreground' : 'border-border'
              )}
            >
              <div className="text-[13px] font-medium truncate" style={{ color: domainColor(t.name) }}>
                {t.name}
              </div>
              <div className="text-[11px] text-muted-foreground truncate">
                {fmtMin(t.minutes_12mo)} 12mo · {t.active_workstreams}/{t.total_workstreams} active
              </div>
            </button>
          ))}
        </div>
        {(domain ? data.domains.filter((x) => x.name === domain) : data.domains).map((t) => {
          const ws = data.workstreams.filter((w) => w.domain === t.name && matches(w.name, w.domain))
          if (!ws.length) return null
          const active = ws.filter((w) => w.state === 'active')
          const inactive = ws.filter((w) => w.state === 'inactive')
          const archived = ws.filter((w) => w.state === 'archived')
          return (
            <details key={t.name} className="border border-border px-2.5 py-1.5 my-1.5" open={!!domain}>
              <summary className="cursor-pointer select-none text-[12px] font-medium text-foreground">
                {t.name} · {active.length} active · {inactive.length} inactive · {archived.length} archived
              </summary>
              {active.map((w) => (
                <WsRow key={w.path} w={w} onOpen={onOpen} />
              ))}
              {inactive.length > 0 && (
                <details className="ml-2.5">
                  <summary className="cursor-pointer select-none text-[11px] text-muted-foreground py-0.5">
                    Inactive ({inactive.length})
                  </summary>
                  {inactive.map((w) => (
                    <WsRow key={w.path} w={w} onOpen={onOpen} />
                  ))}
                </details>
              )}
              {archived.length > 0 && (
                <details className="ml-2.5">
                  <summary className="cursor-pointer select-none text-[11px] text-muted-foreground py-0.5">
                    Archived ({archived.length})
                  </summary>
                  {archived.map((w) => (
                    <WsRow key={w.path} w={w} onOpen={onOpen} />
                  ))}
                </details>
              )}
            </details>
          )
        })}
      </details>

      {/* ── Collapsed: This week (grouped by day) ── */}
      <ThisWeek data={data} inDom={inDom} matches={matches} onOpen={onOpen} />

      {/* ── Collapsed: Recent cards ── */}
      <RecentCards data={data} inDom={inDom} matches={matches} onOpen={onOpen} />
    </div>
  )
}

function WsRow({ w, onOpen }: { w: CockpitWorkstream; onOpen: OpenFn }) {
  const bits: string[] = []
  if (w.minutes_30d) bits.push(`${fmtMin(w.minutes_30d)}/30d`)
  if (w.delta_pct != null) bits.push(`${w.delta_pct > 0 ? '↑' : w.delta_pct < 0 ? '↓' : '→'}${Math.abs(w.delta_pct)}%`)
  if (w.open_blocks) bits.push(`${w.open_blocks} open`)
  if (w.last_activity) bits.push(fmtDay(w.last_activity))
  const spark = w.spark && w.spark.some((v) => v > 0) ? w.spark : null
  const mx = spark ? Math.max(...spark, 1) : 1
  return (
    <Row>
      <button type="button" className={nameCls} onClick={() => onOpen(w.path, w.name, `${w.domain} / ${w.name}`)}>
        {w.name}
      </button>
      {spark && (
        <span className="flex items-end gap-px h-4 flex-shrink-0">
          {spark.map((v, i) => (
            <i
              key={i}
              className="inline-block w-[3px]"
              style={{ height: `${Math.max(1, Math.round((v / mx) * 16))}px`, background: domainColor(w.domain) }}
            />
          ))}
        </span>
      )}
      <span className={metaCls}>{bits.join(' · ')}</span>
    </Row>
  )
}

const ATTENTION_CAP = 7

function AttentionZone({
  data,
  inDom,
  matches,
  onOpen,
  refresh,
}: {
  data: CockpitPayload
  inDom: (x: { domain: string }) => boolean
  matches: (...parts: Array<string | null | undefined>) => boolean
  onOpen: OpenFn
  refresh: () => void
}) {
  const [showAll, setShowAll] = useState(false)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [dismissed, setDismissed] = useState<Set<string>>(new Set())
  const [pending, setPending] = useState<Set<string>>(new Set())
  const [errors, setErrors] = useState<Record<string, string>>({})

  const groups = (data.attention_groups || []).filter(
    (g) => inDom(g) && matches(g.name, g.workstream, g.domain, g.status)
  )
  const total = groups.reduce((n, g) => n + g.count, 0)

  const act = async (path: string, fn: () => Promise<unknown>) => {
    setPending((p) => new Set(p).add(path))
    setErrors((e) => ({ ...e, [path]: '' }))
    try {
      await fn()
      setDismissed((d) => new Set(d).add(path))
      refresh()
    } catch (e) {
      setErrors((prev) => ({ ...prev, [path]: e instanceof Error ? e.message : String(e) }))
    } finally {
      setPending((p) => {
        const n = new Set(p)
        n.delete(path)
        return n
      })
    }
  }

  const itemActions = (g: CockpitAttentionGroup, it: CockpitAttentionGroup['items'][number]) => (
    <>
      {g.status === 'completed' && !it.wrapped && (
        <button
          type="button"
          disabled={pending.has(it.path)}
          className={miniBtnCls}
          onClick={() => void act(it.path, () => enqueueWrapup(it.path))}
        >
          wrap
        </button>
      )}
      {(g.status === 'overdue' || g.status === 'missing') && (
        <button
          type="button"
          disabled={pending.has(it.path)}
          className={miniBtnCls}
          onClick={() => void act(it.path, () => archiveBlockDirect(it.path))}
        >
          archive
        </button>
      )}
    </>
  )

  const renderGroup = (g: CockpitAttentionGroup) => {
    const items = g.items.filter((it) => !dismissed.has(it.path))
    if (!items.length && g.count <= g.items.length) return null
    const key = `${g.domain}/${g.workstream}/${g.name}/${g.status}`
    const single = items.length === 1 && g.count === 1 ? items[0] : null
    return (
      <div key={key}>
        <Row>
          <span
            className="flex-shrink-0 text-[10px] text-white rounded-full px-2 py-px"
            style={{ background: STATUS_COLORS[g.status] || '#a3a3a3' }}
          >
            {g.status.replace('_', ' ')}
          </span>
          <button
            type="button"
            className={nameCls}
            onClick={() => {
              if (single) onOpen(single.path, single.name, `${g.domain} / ${g.workstream || ''}`)
              else
                setExpanded((prev) => {
                  const n = new Set(prev)
                  if (n.has(key)) n.delete(key)
                  else n.add(key)
                  return n
                })
            }}
          >
            {g.count > 1 ? `${g.name} · ×${g.count}` : g.name}
          </button>
          <span className={metaCls}>
            {g.domain} / {g.workstream || ''}
          </span>
          {g.latest_start && <span className={whenCls}>{fmtDay(g.latest_start)}</span>}
          {single && itemActions(g, single)}
        </Row>
        {single && errors[single.path] && (
          <div className="text-[11px] text-red-400 px-1.5 truncate">{errors[single.path]}</div>
        )}
        {!single && expanded.has(key) && (
          <div>
            {items.map((it) => (
              <Row key={it.path} sub>
                <button
                  type="button"
                  className={nameCls}
                  onClick={() => onOpen(it.path, it.name, `${g.domain} / ${g.workstream || ''}`)}
                >
                  {it.name}
                </button>
                {it.start && <span className={whenCls}>{fmtDay(it.start)}</span>}
                {itemActions(g, it)}
                {errors[it.path] && <span className="text-[11px] text-red-400 truncate">{errors[it.path]}</span>}
              </Row>
            ))}
            {g.count > g.items.length && (
              <div className="text-[11px] text-muted-foreground italic px-1.5 py-0.5 ml-6">
                …and {g.count - g.items.length} more
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <section>
      <SectionHeading>
        Needs you · {groups.length}
        {total !== groups.length ? ` (${total} blocks)` : ''}
      </SectionHeading>
      {!groups.length && <div className="text-[12px] text-muted-foreground italic px-1.5 py-1">Nothing needs you.</div>}
      {(showAll ? groups : groups.slice(0, ATTENTION_CAP)).map(renderGroup)}
      {!showAll && groups.length > ATTENTION_CAP && (
        <button type="button" className={cn(miniBtnCls, 'mt-1')} onClick={() => setShowAll(true)}>
          show all {groups.length}
        </button>
      )}
    </section>
  )
}

function RecentZone({
  data,
  inDom,
  matches,
  onOpen,
}: {
  data: CockpitPayload
  inDom: (x: { domain: string }) => boolean
  matches: (...parts: Array<string | null | undefined>) => boolean
  onOpen: OpenFn
}) {
  const rec = (data.recent || []).filter((r) => inDom(r) && matches(r.name, r.workstream, r.domain))
  if (!rec.length) return null
  return (
    <section>
      <SectionHeading>Pick up where you left off</SectionHeading>
      {rec.map((r) => (
        <Row key={r.path}>
          {r.kind === 'card' ? (
            <span className="flex-shrink-0 text-[9px] border border-border px-1 text-muted-foreground">
              {(r.name.split('.').pop() || '?').toUpperCase().slice(0, 4)}
            </span>
          ) : (
            <span
              className="flex-shrink-0 w-2 h-2 rounded-full"
              style={{ background: STATUS_COLORS[r.status || ''] || '#a3a3a3' }}
            />
          )}
          <button
            type="button"
            className={nameCls}
            onClick={() => onOpen(r.path, r.name, `${r.domain}${r.workstream ? ` / ${r.workstream}` : ''}`)}
          >
            {r.name}
          </button>
          <span className={metaCls}>
            {r.domain}
            {r.workstream ? ` / ${r.workstream}` : ''}
          </span>
          <span className={whenCls}>{relTime(r.mtime)}</span>
        </Row>
      ))}
    </section>
  )
}

function ThisWeek({
  data,
  inDom,
  matches,
  onOpen,
}: {
  data: CockpitPayload
  inDom: (x: { domain: string }) => boolean
  matches: (...parts: Array<string | null | undefined>) => boolean
  onOpen: OpenFn
}) {
  const up = data.upcoming.filter((b) => inDom(b) && matches(b.name, b.workstream, b.domain))
  const byDay = new Map<string, typeof up>()
  for (const b of up) {
    const k = fmtDay(b.start)
    if (!byDay.has(k)) byDay.set(k, [])
    byDay.get(k)!.push(b)
  }
  return (
    <details className="mt-3 border border-border px-3 py-2">
      <summary className="cursor-pointer select-none text-[13px] text-foreground">This week · {up.length}</summary>
      {[...byDay.entries()].map(([k, blocks]) => (
        <div key={k}>
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground mt-2 px-1.5">{k}</div>
          {blocks.map((b) => (
            <Row key={b.path}>
              <span className={cn(whenCls, 'min-w-[56px]')}>{b.all_day ? 'all-day' : fmtTime(b.start)}</span>
              <button
                type="button"
                className={nameCls}
                onClick={() => onOpen(b.path, b.name, `${b.domain} / ${b.workstream || ''}`)}
              >
                {stripTimestamp(b.name)}
              </button>
              <span className={metaCls}>
                {b.domain} / {b.workstream || ''}
              </span>
            </Row>
          ))}
        </div>
      ))}
    </details>
  )
}

function RecentCards({
  data,
  inDom,
  matches,
  onOpen,
}: {
  data: CockpitPayload
  inDom: (x: { domain: string }) => boolean
  matches: (...parts: Array<string | null | undefined>) => boolean
  onOpen: OpenFn
}) {
  const cards = data.cards.filter((c) => inDom(c) && matches(c.name, c.workstream, c.domain))
  return (
    <details className="mt-3 border border-border px-3 py-2">
      <summary className="cursor-pointer select-none text-[13px] text-foreground">
        Recent cards · {cards.length}
      </summary>
      {cards.slice(0, 40).map((c) => (
        <Row key={c.path}>
          <span className="flex-shrink-0 text-[9px] border border-border px-1 text-muted-foreground">
            {(c.ext || '?').toUpperCase()}
          </span>
          <button
            type="button"
            className={nameCls}
            onClick={() => onOpen(c.path, c.name, `${c.domain}${c.workstream ? ` / ${c.workstream}` : ''}`)}
          >
            {c.name}
          </button>
          <span className={metaCls}>
            {c.domain}
            {c.workstream ? ` / ${c.workstream}` : ''}
          </span>
        </Row>
      ))}
    </details>
  )
}
